import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link as RouterLink } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight, Library, Loader2, Plus, Trash2 } from 'lucide-react';
import type { ModuleRef, Source } from '@/lib/models/source';
import { SourceKindLabels } from '@/lib/models/source';
import { sourcesApi } from '@/lib/api/sourcesApi';
import { SourceIcon } from './SourceIcon';
import { useGroupedSources } from './useGroupedSources';
import { formatBytes, formatRelativeDate } from './sourceFormat';

// SourceList — variant-aware source library renderer. Two surfaces:
//
//   variant="compact"  — the chat-composer side rail (collapsed icon
//                        column / expandable to ~280px). Owns its
//                        own expanded + search state + collapsed-mode
//                        hover-peek overlay. Lives inside LinkAgentModal.
//
//   variant="full"     — the dashboard /sources page list. Wider table
//                        layout with extra columns (kind label, size,
//                        uploaded date, org chip). Phase 3 wires the
//                        upload + delete actions; Phase 4 wires the
//                        viewer modal. For now both variants surface
//                        clicks via onSourceClick so the shared modal
//                        can be wired from either entry point.
//
// onAddMention is compact-only (the "+" button next to each row when
// the rail is expanded — inserts an @-source reference into the chat
// composer). The full variant ignores it.

interface SourceListProps {
    variant: 'compact' | 'full';
    sources: Source[];
    onSourceClick?: (s: Source) => void;
    // compact-only
    onAddMention?: (s: Source) => void;
    // compact-only — set of source IDs currently referenced by an
    // `@"name"` mention in the chat composer. Highlighted rows get a
    // brand-teal ring so the user sees Link "sees" the mention.
    // Phase 5 B1.
    highlightedIds?: Set<string>;
    // compact-only — Phase 5 B3 (extended) — upload files directly
    // to the library from inside the chat sidebar. Wired to two
    // surfaces: the "+" button in the rail header (file picker) and
    // a drop zone that wraps the whole rail (drag/drop). Omit to
    // hide both affordances; the chat-level drop zone (handled by
    // the parent) still works for "drag to attach + optionally save".
    onUploadToLibrary?: (files: File[]) => Promise<void> | void;
    // compact-only — top padding (px) so the rail starts below a floating
    // overlay header (Link's gradient header). Default 0.
    topInset?: number;
    // full-only — per-row trash button visible on hover. Page owns
    // any confirmation dialog before calling this. Omit to hide the
    // delete action entirely.
    onDelete?: (s: Source) => void;
    // full-only — search filter (controlled by the page). Empty
    // string = no filter. Match is case-insensitive substring on
    // original_name; same shape the compact rail uses internally.
    search?: string;
}

export const SourceList: React.FC<SourceListProps> = ({
    variant,
    sources,
    onSourceClick,
    onAddMention,
    highlightedIds,
    onUploadToLibrary,
    topInset,
    onDelete,
    search,
}) => {
    if (variant === 'compact') {
        return (
            <CompactRail
                sources={sources}
                onSourceClick={onSourceClick}
                onAddMention={onAddMention}
                highlightedIds={highlightedIds}
                onUploadToLibrary={onUploadToLibrary}
                topInset={topInset}
            />
        );
    }
    return (
        <FullTable
            sources={sources}
            onSourceClick={onSourceClick}
            onDelete={onDelete}
            search={search ?? ''}
        />
    );
};

// ─── Compact rail (chat sidebar) ────────────────────────────────────

const CompactRail: React.FC<{
    sources: Source[];
    onSourceClick?: (s: Source) => void;
    onAddMention?: (s: Source) => void;
    highlightedIds?: Set<string>;
    onUploadToLibrary?: (files: File[]) => Promise<void> | void;
    topInset?: number;
}> = ({ sources, onSourceClick, onAddMention, highlightedIds, onUploadToLibrary, topInset }) => {
    const [expanded, setExpanded] = useState(false);
    const [search, setSearch] = useState('');
    const [hoveredSource, setHoveredSource] = useState<{ source: Source; rect: DOMRect } | null>(null);
    const peekTimerRef = useRef<number | null>(null);
    const scrollerRef = useRef<HTMLDivElement | null>(null);
    // Phase 5 B3 (extended) — drop-zone state + file picker ref.
    // Depth counter so dragenter/leave on inner children don't toggle
    // the overlay flicker. stopPropagation in the handlers prevents
    // the chat-level drop zone (parent LinkAgentModal) from also firing.
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const dragDepthRef = useRef(0);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const openFilePicker = () => fileInputRef.current?.click();

    // ── Resizable rail (#229) ──────────────────────────────────────────
    // Drag the right edge to widen the expanded rail; width persists. Clamped
    // so it can't crowd out the chat. Collapsed mode keeps the fixed 56px.
    const RAIL_MIN = 240, RAIL_MAX = 560, RAIL_DEFAULT = 280;
    const RAIL_KEY = 'nexilink:link_rail_width';
    const [railWidth, setRailWidth] = useState<number>(() => {
        const raw = Number(localStorage.getItem(RAIL_KEY));
        return raw >= RAIL_MIN && raw <= RAIL_MAX ? raw : RAIL_DEFAULT;
    });
    const [resizing, setResizing] = useState(false);
    useEffect(() => {
        try { localStorage.setItem(RAIL_KEY, String(railWidth)); } catch { /* ignore */ }
    }, [railWidth]);
    const startResize = (e: React.MouseEvent) => {
        e.preventDefault();
        const startX = e.clientX;
        const startW = railWidth;
        setResizing(true);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        const onMove = (ev: MouseEvent) => {
            const next = Math.min(RAIL_MAX, Math.max(RAIL_MIN, startW + (ev.clientX - startX)));
            setRailWidth(next);
        };
        const onUp = () => {
            setResizing(false);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };
    const handlePickedFiles = (files: FileList | null) => {
        if (!files || files.length === 0 || !onUploadToLibrary) return;
        const arr = Array.from(files);
        // Best-effort; the caller decides error UI. Resetting the
        // input value lets the user pick the same file twice (browser
        // dedup defaults to ignoring identical re-picks otherwise).
        void onUploadToLibrary(arr);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };
    const handleRailDragEnter = (e: React.DragEvent) => {
        if (!onUploadToLibrary) return;
        if (!Array.from(e.dataTransfer.types ?? []).includes('Files')) return;
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current += 1;
        setIsDraggingOver(true);
    };
    const handleRailDragLeave = (e: React.DragEvent) => {
        if (!onUploadToLibrary) return;
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) setIsDraggingOver(false);
    };
    const handleRailDragOver = (e: React.DragEvent) => {
        if (!onUploadToLibrary) return;
        // Always preventDefault during the drag to permit a drop here.
        // Skipping it (e.g. by gating on isDraggingOver) is a subtle
        // bug — dragenter and dragover fire near-simultaneously, and
        // a state-set in dragenter hasn't committed by the time the
        // first dragover handler runs. The closure still sees the old
        // isDraggingOver=false → no preventDefault → browser rejects
        // the eventual drop. preventDefault should be unconditional
        // whenever this surface accepts uploads.
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
    };
    const handleRailDrop = (e: React.DragEvent) => {
        if (!onUploadToLibrary) return;
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current = 0;
        setIsDraggingOver(false);
        const files = Array.from(e.dataTransfer.files ?? []);
        if (files.length === 0) return;
        void onUploadToLibrary(files);
    };

    const grouped = useGroupedSources(sources, search);

    // Collapsed-rail hover-peek: 200ms show-delay + 120ms hide-delay
    // so the cursor can cross the gap from icon → card without
    // dismissing. Same timer ref handles both directions.
    const schedulePeek = (source: Source, target: HTMLElement) => {
        if (peekTimerRef.current !== null) window.clearTimeout(peekTimerRef.current);
        peekTimerRef.current = window.setTimeout(() => {
            setHoveredSource({ source, rect: target.getBoundingClientRect() });
        }, 200);
    };
    const cancelPeek = () => {
        if (peekTimerRef.current !== null) window.clearTimeout(peekTimerRef.current);
        peekTimerRef.current = window.setTimeout(() => setHoveredSource(null), 120);
    };
    const dismissPeek = () => {
        if (peekTimerRef.current !== null) window.clearTimeout(peekTimerRef.current);
        setHoveredSource(null);
    };

    // Dismiss the peek whenever the rail expands — the card is a
    // collapsed-state affordance only; expanded mode has its own
    // row layout.
    useEffect(() => {
        if (expanded) dismissPeek();
    }, [expanded]);

    // Phase 5 B3 (extended) — both populated AND empty libraries
    // render the same shell with a "+" button in the header for
    // click-to-upload + a drag/drop zone wrapping the whole rail.
    // The empty branch additionally shows an explanatory paragraph
    // and a prominent "Lägg till källor" button. The chat-level drop
    // zone (LinkAgentModal) handles drops OUTSIDE the rail — its
    // dragenter is suppressed when the user is over the rail thanks
    // to stopPropagation in handleRailDrag*.
    if (sources.length === 0) {
        return (
            <div
                className="relative z-[2] flex-shrink-0 flex flex-col border-r border-gray-200 bg-white transition-[width] duration-200 ease-out"
                // Raised above the chat (z-[2] vs chat's z-[1]) so this right-cast
                // shadow lands ON the chat — the source lib reads as elevated.
                style={{ width: expanded ? railWidth : 56, paddingTop: topInset, boxShadow: '4px 0 13px -5px rgba(0,0,0,0.10)' }}
                onDragEnter={handleRailDragEnter}
                onDragLeave={handleRailDragLeave}
                onDragOver={handleRailDragOver}
                onDrop={handleRailDrop}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    onChange={(e) => handlePickedFiles(e.target.files)}
                />
                {isDraggingOver && (
                    <div className="absolute inset-0 z-30 bg-[#1A4D4F]/10 border-2 border-dashed border-[#1A4D4F] rounded-r-lg flex items-center justify-center pointer-events-none">
                        {expanded ? (
                            <div className="text-center px-2">
                                <Plus className="w-5 h-5 text-[#1A4D4F] mx-auto mb-1" />
                                <span className="text-[11px] font-medium text-[#1A4D4F]">
                                    Släpp för att lägga till i biblioteket
                                </span>
                            </div>
                        ) : (
                            <Plus className="w-5 h-5 text-[#1A4D4F]" />
                        )}
                    </div>
                )}
                <div className={`flex items-center gap-2 py-2 border-b border-gray-200/60 ${expanded ? 'px-2' : 'justify-center px-0'}`}>
                    {expanded ? (
                        <>
                            <Library className="w-4 h-4 text-[#1A4D4F] flex-shrink-0 ml-0.5" />
                            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                                Källor (0)
                            </span>
                            {onUploadToLibrary && (
                                <button
                                    type="button"
                                    onClick={openFilePicker}
                                    title="Lägg till källa"
                                    className="ml-auto flex items-center justify-center w-6 h-6 rounded hover:bg-[#1A4D4F] hover:text-white text-[#1A4D4F] transition-colors"
                                >
                                    <Plus className="w-3.5 h-3.5" />
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => setExpanded(false)}
                                title="Fäll ihop biblioteket"
                                className={
                                    'flex items-center justify-center w-6 h-6 rounded hover:bg-gray-200/60 text-gray-400 hover:text-gray-700 ' +
                                    (onUploadToLibrary ? '' : 'ml-auto')
                                }
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                        </>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setExpanded(true)}
                            title="Källor — klicka för att lägga till"
                            className="flex items-center justify-center w-7 h-7 rounded hover:bg-gray-200/60 text-[#1A4D4F]"
                        >
                            <Library className="w-4 h-4" />
                        </button>
                    )}
                </div>
                {expanded && (
                    // Phase 5 E3 — friendlier empty state: an icon badge +
                    // heading make the empty rail feel intentional rather
                    // than barren, and the CTA is a solid primary button
                    // (it's the one action here).
                    <div className="flex flex-col items-center px-3 py-6 text-center gap-2.5">
                        <div className="flex items-center justify-center w-11 h-11 rounded-full bg-[#1A4D4F]/10 text-[#1A4D4F]">
                            <Library className="w-5 h-5" />
                        </div>
                        <div className="space-y-0.5">
                            <p className="text-xs font-semibold text-gray-700">
                                Inga källor ännu
                            </p>
                            <p className="text-[11px] text-gray-500 leading-snug">
                                Lägg till PDF:er, text, kod eller länkar att referera i chatten.
                            </p>
                        </div>
                        {onUploadToLibrary ? (
                            <button
                                type="button"
                                onClick={openFilePicker}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-[#1A4D4F] rounded-lg shadow-sm hover:bg-[#1A4D4F]/90 transition-colors"
                            >
                                <Plus className="w-3.5 h-3.5" /> Lägg till källor
                            </button>
                        ) : (
                            <RouterLink
                                to="/dashboard/library"
                                className="inline-flex items-center gap-1 text-[11px] font-medium text-[#1A4D4F] hover:text-[#1A4D4F]/80 hover:underline"
                            >
                                Öppna biblioteket →
                            </RouterLink>
                        )}
                        <p className="text-[10px] text-gray-400 leading-snug">
                            Referera med <code className="text-[10px] bg-gray-100 px-1 rounded">@</code> · eller dra filer hit
                        </p>
                    </div>
                )}
            </div>
        );
    }

    return (
        <>
            {hoveredSource && createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: hoveredSource.rect.top + hoveredSource.rect.height / 2,
                        left: hoveredSource.rect.right + 4,
                        transform: 'translateY(-50%)',
                        zIndex: 60,
                    }}
                    onMouseEnter={() => {
                        if (peekTimerRef.current !== null) window.clearTimeout(peekTimerRef.current);
                    }}
                    onMouseLeave={cancelPeek}
                    className="flex items-center gap-2 px-2 py-1.5 bg-white border border-gray-200 rounded-lg shadow-lg text-xs"
                >
                    <SourceIcon kind={hoveredSource.source.kind} />
                    <span className="text-gray-800 max-w-[340px] break-words">
                        {hoveredSource.source.original_name}
                    </span>
                    {hoveredSource.source.is_promoted && (
                        <span className="text-[9px] text-gray-400 uppercase tracking-wide">org</span>
                    )}
                    {onAddMention && (
                        <button
                            type="button"
                            onClick={() => {
                                onAddMention(hoveredSource.source);
                                dismissPeek();
                            }}
                            title="Lägg till @-referens i meddelandet"
                            className="flex items-center justify-center w-5 h-5 rounded text-gray-400 hover:bg-[#1A4D4F] hover:text-white transition-colors"
                        >
                            <Plus className="w-3 h-3" />
                        </button>
                    )}
                </div>,
                document.body,
            )}

            <div
                className={
                    // Raised above the chat (z-[2] vs chat's z-[1]) so the right-cast
                    // boxShadow below lands ON the chat — the source lib reads as
                    // elevated. NOTE: this is the POPULATED branch (the one users
                    // with a non-empty library actually see); the empty branch above
                    // carries the same z-[2]/boxShadow. Keep them in sync.
                    'relative z-[2] flex-shrink-0 flex flex-col border-r border-gray-200 bg-white ease-out ' +
                    (resizing ? '' : 'transition-[width] duration-200 ')
                }
                style={{ width: expanded ? railWidth : 56, paddingTop: topInset, boxShadow: '4px 0 13px -5px rgba(0,0,0,0.10)' }}
                onDragEnter={handleRailDragEnter}
                onDragLeave={handleRailDragLeave}
                onDragOver={handleRailDragOver}
                onDrop={handleRailDrop}
            >
                {/* Resize handle — drag the right edge to widen the rail (#229).
                    Only when expanded; invisible until hover/drag. */}
                {expanded && (
                    <div
                        onMouseDown={startResize}
                        title="Dra för att ändra bredd"
                        className={`absolute top-0 right-0 h-full w-1.5 -mr-0.5 z-30 cursor-col-resize transition-colors ${
                            resizing ? 'bg-[#1A4D4F]/30' : 'hover:bg-[#1A4D4F]/20'
                        }`}
                    />
                )}
                {/* Same hidden file input + drop overlay pattern as the
                    empty branch. Both branches use the same drag-state
                    + handlers so the visual feels identical regardless
                    of whether the library is empty or full. */}
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    onChange={(e) => handlePickedFiles(e.target.files)}
                />
                {isDraggingOver && (
                    <div className="absolute inset-0 z-30 bg-[#1A4D4F]/10 border-2 border-dashed border-[#1A4D4F] rounded-r-lg flex items-center justify-center pointer-events-none">
                        {expanded ? (
                            <div className="text-center px-2">
                                <Plus className="w-5 h-5 text-[#1A4D4F] mx-auto mb-1" />
                                <span className="text-[11px] font-medium text-[#1A4D4F]">
                                    Släpp för att lägga till i biblioteket
                                </span>
                            </div>
                        ) : (
                            <Plus className="w-5 h-5 text-[#1A4D4F]" />
                        )}
                    </div>
                )}
                <div className={`flex items-center gap-2 py-2 border-b border-gray-200/60 ${expanded ? 'px-2' : 'justify-center px-0'}`}>
                    {expanded ? (
                        <>
                            <Library className="w-4 h-4 text-[#1A4D4F] flex-shrink-0 ml-0.5" />
                            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                                Källor ({sources.length})
                            </span>
                            {onUploadToLibrary && (
                                <button
                                    type="button"
                                    onClick={openFilePicker}
                                    title="Lägg till källa"
                                    className="ml-auto flex items-center justify-center w-6 h-6 rounded hover:bg-[#1A4D4F] hover:text-white text-[#1A4D4F] transition-colors"
                                >
                                    <Plus className="w-3.5 h-3.5" />
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => setExpanded(false)}
                                title="Fäll ihop biblioteket"
                                className={
                                    'flex items-center justify-center w-6 h-6 rounded hover:bg-gray-200/60 text-gray-400 hover:text-gray-700 ' +
                                    (onUploadToLibrary ? '' : 'ml-auto')
                                }
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                        </>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setExpanded(true)}
                            title="Öppna biblioteket"
                            className="flex items-center justify-center w-7 h-7 rounded hover:bg-gray-200/60 text-[#1A4D4F]"
                        >
                            <Library className="w-4 h-4" />
                        </button>
                    )}
                </div>
                {expanded && (
                    <div className="px-2 py-1.5 border-b border-gray-200/60">
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Sök i biblioteket…"
                            autoFocus
                            className="w-full text-sm px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent transition-shadow"
                        />
                    </div>
                )}
                <div
                    ref={scrollerRef}
                    onScroll={dismissPeek}
                    className="flex-1 overflow-y-auto px-1 py-2"
                >
                    {expanded && grouped.total === 0 ? (
                        <div className="px-2 py-3 text-[10px] text-gray-400 text-center">
                            Inga träffar
                        </div>
                    ) : (
                        <>
                            {grouped.visual.length > 0 && (
                                <RailGroup
                                    label="PDF & bilder"
                                    sources={grouped.visual}
                                    expanded={expanded}
                                    onSourceClick={onSourceClick}
                                    onAddMention={onAddMention}
                                    highlightedIds={highlightedIds}
                                    onIconHover={schedulePeek}
                                    onIconLeave={cancelPeek}
                                />
                            )}
                            {grouped.text.length > 0 && (
                                <RailGroup
                                    label="Text & kod"
                                    sources={grouped.text}
                                    expanded={expanded}
                                    onSourceClick={onSourceClick}
                                    onAddMention={onAddMention}
                                    highlightedIds={highlightedIds}
                                    onIconHover={schedulePeek}
                                    onIconLeave={cancelPeek}
                                />
                            )}
                            {grouped.url.length > 0 && (
                                <RailGroup
                                    label="Länkar"
                                    sources={grouped.url}
                                    expanded={expanded}
                                    onSourceClick={onSourceClick}
                                    onAddMention={onAddMention}
                                    highlightedIds={highlightedIds}
                                    onIconHover={schedulePeek}
                                    onIconLeave={cancelPeek}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

// One kind-grouped section of the rail. Same component for collapsed
// + expanded; the `expanded` prop swaps the per-row layout (icon-only
// vs icon + name + add button).
const RailGroup: React.FC<{
    label: string;
    sources: Source[];
    expanded: boolean;
    onSourceClick?: (s: Source) => void;
    onAddMention?: (s: Source) => void;
    highlightedIds?: Set<string>;
    onIconHover?: (s: Source, target: HTMLElement) => void;
    onIconLeave?: () => void;
}> = ({ label, sources, expanded, onSourceClick, onAddMention, highlightedIds, onIconHover, onIconLeave }) => (
    <div className="mb-3 last:mb-0">
        {expanded && (
            <p className="px-2 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                {label}
            </p>
        )}
        <div className="space-y-0.5">
            {sources.map((s) => {
                // Phase 5 B1 — a brand-teal ring + tinted background
                // signals "this source is currently referenced via an
                // @-mention in the composer." Same source object regardless
                // of group; the highlight tracks the input's text in real
                // time. transition-colors below handles the fade.
                const isHighlighted = highlightedIds?.has(s.id) ?? false;
                return (
                <div
                    key={s.id}
                    title={expanded ? s.original_name : undefined}
                    onMouseEnter={onIconHover ? (e) => onIconHover(s, e.currentTarget) : undefined}
                    onMouseLeave={onIconLeave}
                    onClick={onSourceClick ? () => onSourceClick(s) : undefined}
                    className={
                        'group flex items-center rounded transition-colors text-sm ' +
                        (isHighlighted
                            ? 'text-[#1A4D4F] bg-[#1A4D4F]/10 ring-1 ring-[#1A4D4F]/40 hover:bg-[#1A4D4F]/15 '
                            : 'text-gray-800 hover:bg-white hover:shadow-sm ') +
                        (onSourceClick ? 'cursor-pointer ' : '') +
                        (expanded ? 'gap-2 px-2 py-2' : 'justify-center p-2')
                    }
                >
                    <SourceIcon kind={s.kind} />
                    {expanded && (
                        <>
                            <span className="flex-1 truncate">{s.original_name}</span>
                            {s.is_promoted && (
                                <span className="text-[9px] text-gray-400 uppercase tracking-wide">
                                    org
                                </span>
                            )}
                            {onAddMention && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onAddMention(s);
                                    }}
                                    title="Lägg till @-referens i meddelandet"
                                    className={
                                        'flex-shrink-0 flex items-center justify-center w-5 h-5 rounded transition-all ' +
                                        (isHighlighted
                                            ? 'text-[#1A4D4F]'
                                            : 'text-gray-400 opacity-0 group-hover:opacity-100 hover:bg-[#1A4D4F] hover:text-white')
                                    }
                                >
                                    <Plus className="w-3 h-3" />
                                </button>
                            )}
                        </>
                    )}
                </div>
                );
            })}
        </div>
    </div>
);

// ─── Full table (dashboard library) ─────────────────────────────────

// Phase 2 ships the rendering shape with the four agreed columns
// (filename, kind, size, uploaded). Phase 3 wires the page in
// (route + nav entry + upload/delete actions). Phase 4 wires the
// shared viewer modal.

const FullTable: React.FC<{
    sources: Source[];
    onSourceClick?: (s: Source) => void;
    onDelete?: (s: Source) => void;
    search: string;
}> = ({ sources, onSourceClick, onDelete, search }) => {
    const grouped = useGroupedSources(sources, search);
    const colSpan = onDelete ? 5 : 4;

    if (sources.length === 0) {
        return (
            <div className="text-center py-12 text-sm text-gray-500">
                Inga källor i biblioteket ännu.
            </div>
        );
    }
    if (grouped.total === 0) {
        return (
            <div className="text-center py-12 text-sm text-gray-500">
                Inga träffar för &ldquo;{search}&rdquo;.
            </div>
        );
    }

    // Kind groups in the same order as the compact rail so the two
    // surfaces feel like one library.
    const groups: Array<{ label: string; rows: Source[] }> = [
        { label: 'PDF & bilder', rows: grouped.visual },
        { label: 'Text & kod', rows: grouped.text },
        { label: 'Länkar', rows: grouped.url },
    ];

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                        <th className="text-left font-semibold py-2 px-3">Namn</th>
                        <th className="text-left font-semibold py-2 px-3 w-24">Typ</th>
                        <th className="text-right font-semibold py-2 px-3 w-24">Storlek</th>
                        <th className="text-left font-semibold py-2 px-3 w-36">Uppladdad</th>
                        {onDelete && <th className="w-12" aria-label="Åtgärder" />}
                    </tr>
                </thead>
                <tbody>
                    {groups.map((g) =>
                        g.rows.length === 0 ? null : (
                            <React.Fragment key={g.label}>
                                <tr className="bg-gray-50/60">
                                    <td
                                        colSpan={colSpan}
                                        className="py-1.5 px-3 text-[10px] font-semibold text-gray-500 uppercase tracking-wider"
                                    >
                                        {g.label} ({g.rows.length})
                                    </td>
                                </tr>
                                {g.rows.map((s) => (
                                    <FullTableRow
                                        key={s.id}
                                        source={s}
                                        onClick={onSourceClick}
                                        onDelete={onDelete}
                                    />
                                ))}
                            </React.Fragment>
                        ),
                    )}
                </tbody>
            </table>
        </div>
    );
};

// One full-variant row. Owns its own "used in" expand state + lazy
// module-list cache so opening one row doesn't fan out fetches across
// the whole table. The chevron + label flips between collapsed and
// expanded; the chip strip below renders inline once the list is in.
const FullTableRow: React.FC<{
    source: Source;
    onClick?: (s: Source) => void;
    onDelete?: (s: Source) => void;
}> = ({ source: s, onClick, onDelete }) => {
    const [expanded, setExpanded] = useState(false);
    const [modules, setModules] = useState<ModuleRef[] | null>(null);
    const [loading, setLoading] = useState(false);

    const toggleExpanded = (e: React.MouseEvent) => {
        e.stopPropagation();
        // Lazy fetch on first expand. Errors silently leave the list
        // null — clicking the link again retries; the row's primary
        // action (opening the viewer) still works.
        if (!expanded && modules === null && !loading) {
            setLoading(true);
            sourcesApi
                .listModulesUsingSource(s.id)
                .then((refs) => setModules(refs))
                .catch(() => setModules(null))
                .finally(() => setLoading(false));
        }
        setExpanded((v) => !v);
    };

    return (
        <tr
            onClick={onClick ? () => onClick(s) : undefined}
            className={
                'group border-b border-gray-100 transition-colors ' +
                (onClick ? 'cursor-pointer hover:bg-gray-50' : '')
            }
        >
            <td className="py-3 px-3 align-top">
                <div className="flex items-start gap-2 min-w-0">
                    <div className="mt-0.5 flex-shrink-0">
                        <SourceIcon kind={s.kind} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                            <span className="truncate text-gray-800">{s.original_name}</span>
                            {s.is_promoted && (
                                <span className="flex-shrink-0 text-[9px] text-gray-500 uppercase tracking-wide border border-gray-200 rounded px-1.5 py-0.5">
                                    org
                                </span>
                            )}
                        </div>

                        {/* References sub-line. Click toggles inline
                            expansion of the actual module list below
                            — saves opening the viewer just to see
                            which modules reference the source. Future
                            reference types (courses, conversations…)
                            extend the same pattern. */}
                        {s.used_in_count > 0 && (
                            <>
                                <button
                                    type="button"
                                    onClick={toggleExpanded}
                                    className="mt-1 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[#1A4D4F]"
                                >
                                    {expanded ? (
                                        <ChevronDown className="w-3 h-3" />
                                    ) : (
                                        <ChevronRight className="w-3 h-3" />
                                    )}
                                    <span className="hover:underline">
                                        Används i {s.used_in_count}{' '}
                                        {s.used_in_count === 1 ? 'modul' : 'moduler'}
                                    </span>
                                </button>

                                {expanded && (
                                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                                        {loading && (
                                            <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                                                <Loader2 className="w-3 h-3 animate-spin" />
                                                Hämtar…
                                            </span>
                                        )}
                                        {!loading && modules && modules.length > 0 &&
                                            modules.map((m) => (
                                                <RouterLink
                                                    key={m.id}
                                                    to={`/dashboard/onboarding/modules/${m.id}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="inline-flex items-center px-2 py-0.5 text-xs bg-[#1A4D4F]/10 hover:bg-[#1A4D4F] hover:text-white text-[#1A4D4F] rounded transition-colors"
                                                >
                                                    {m.module_name}
                                                </RouterLink>
                                            ))}
                                        {!loading && modules === null && (
                                            <span className="text-xs text-gray-400">
                                                Kunde inte hämta listan.
                                            </span>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </td>
            <td className="py-3 px-3 align-top text-gray-600">
                {SourceKindLabels[s.kind]}
            </td>
            <td className="py-3 px-3 align-top text-right text-gray-600 tabular-nums">
                {formatBytes(s.size_bytes)}
            </td>
            <td className="py-3 px-3 align-top text-gray-600">
                {formatRelativeDate(s.created_at)}
            </td>
            {onDelete && (
                <td className="py-3 px-2 align-top text-right">
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(s);
                        }}
                        title="Radera källan"
                        className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded text-gray-400 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-600 transition-all"
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                </td>
            )}
        </tr>
    );
};
